import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Bot, Send, Loader2, User as UserIcon, CheckCircle2, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "Bu ay ne kadar ödeme var?",
  "Garanti kartının borcunu ödendi olarak işaretle",
  "Ziraat kredisine bir taksit öde",
  "Ödeme planına 15.11.2026 vadeli 50.000 ₺ ekle",
];

export default function Assistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Merhaba! Ben İstasyonFinans asistanınızım. Kredi/kart ödemelerinizi, ödeme planınızı yazarak yönetebilirsiniz. Ne yapmamı istersiniz?", actions: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, actions: r.data.actions || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiErrorDetail(e.response?.data?.detail) || "Bir hata oluştu, tekrar deneyin.", actions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-3xl flex flex-col h-[calc(100vh-0px)] md:h-screen" data-testid="assistant-page">
      <PageHeader
        title="Yapay Zeka Asistanı"
        subtitle="Gemini destekli asistan. Yazın, gerekli işlemi sizin için sistemde bulup uygulasın."
        testid="assistant-header"
      />

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="assistant-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-[#1B2430]" : "bg-[#7C3AED]/20"}`}>
              {m.role === "user" ? <UserIcon className="w-4 h-4 text-[#94A3B8]" /> : <Bot className="w-4 h-4 text-[#A78BFA]" />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#E65100] text-white" : "bg-[#111620] border border-[#253043] text-[#E2E8F0]"}`}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.actions?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {m.actions.map((a, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-[12px] text-[#34D399]">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> {a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center"><Bot className="w-4 h-4 text-[#A78BFA]" /></div>
            <div className="bg-[#111620] border border-[#253043] rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-[#A78BFA]" /></div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} onClick={() => send(s)} data-testid="assistant-suggestion" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-[#111620] border border-[#253043] text-[#94A3B8] hover:border-[#7C3AED] hover:text-white transition-colors">
              <Sparkles className="w-3.5 h-3.5 text-[#A78BFA]" /> {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 pb-4 md:pb-0">
        <textarea
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Örn: Akbank kredisine bir taksit öde..."
          className="flex-1 resize-none px-4 py-3 rounded-2xl bg-[#111620] border border-[#253043] focus:border-[#7C3AED] outline-none transition-colors text-sm max-h-32"
        />
        <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="assistant-send" className="w-12 h-12 rounded-2xl bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white flex items-center justify-center shrink-0 transition-colors">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
